import { TActivityDataProvider } from './dataProviders';
import { ITimeBoundsMetadata, RealmConnectionType } from './sharedTypes';

// events between these next two numbers are considered to be periods.
// we do not include these in the overall event list, but fake their
// existence, since they are dynamic.
export const DYNAMIC_PERIOD_EVENT_START = 90_000;
export const DYNAMIC_PERIOD_EVENT_END = 99_999;

// events between these next two numbers are considered to be part
// of the bracket system. They are not included in the overall event
// list, because they are dynamic.
export const DYNAMIC_BRACKET_EVENT_START = 1_000_000;
export const DYNAMIC_BRACKET_EVENT_END = 5_000_000;

export const MAX_PARTITIONS_PER_SEASON = 16;
export const SEASON_PARTITION_EVENT_START = 100_000;
export const SEASON_PARTITION_EVENT_END = 199_999;

export const MAX_ALLOWED_ACTIVE_SEASON = 5;

export type TSeasonIdentifier =
  | 'season-mn-2'
  | 'season-mn-2-tr'
  | 'season-mn-2-ptr'
  | 'season-mn-1'
  | 'season-mn-1-tr'
  | 'season-mn-1-beta'
  | 'season-mop-2'
  | 'season-mop-1'
  | 'season-mop-1-beta'
  | 'season-tww-3-legion-remix'
  | 'season-tww-3-legion-remix-1-player'
  | 'season-tww-3-break-the-meta'
  | 'season-tww-3'
  | 'season-tww-3-cutoffs'
  | 'season-tww-3-ptr'
  | 'season-tww-3-tr'
  | 'season-tww-2-break-the-meta'
  | 'season-tww-2-meta-vs-meta'
  | 'season-tww-2'
  | 'season-tww-2-ptr'
  | 'season-tww-2-tr'
  | 'season-tww-1-cutoffs'
  | 'season-tww-1-post'
  | 'season-tww-1-break-the-meta'
  | 'season-tww-1-tr'
  | 'season-tww-1-beta'
  | 'season-tww-1'
  | 'season-df-4'
  | 'season-df-4-cutoffs'
  | 'season-df-4-break-the-meta'
  | 'season-df-4-post'
  | 'season-df-4-ptr'
  | 'season-df-4-tr'
  | 'season-df-3-break-the-meta'
  | 'season-df-3-meta-vs-meta'
  | 'season-df-3'
  | 'season-df-3-tr'
  | 'season-df-3-ptr'
  | 'season-df-2-cutoffs'
  | 'season-df-2-post'
  | 'season-df-2-break-the-meta'
  | 'season-df-2-post-1017'
  | 'season-df-2-post-1015'
  | 'season-df-2-tgp'
  | 'season-df-1-cutoffs'
  | 'season-df-1-post'
  | 'season-df-1-break-the-meta'
  | 'season-df-2'
  | 'season-df-1'
  | 'season-sl-4'
  | 'season-sl-4-post'
  | 'season-sl-4-patch-10-0'
  | 'season-sl-4-break-the-meta'
  | 'season-sl-4-legion-timewalking'
  | 'season-sl-3-post-925'
  | 'season-sl-3'
  | 'season-sl-3-legion-timewalking'
  | 'season-sl-2-tormented-hero'
  | 'season-sl-2-post'
  | 'season-sl-2-legion-timewalking'
  | 'season-sl-2-post-915'
  | 'season-sl-2'
  | 'season-sl-1-post'
  | 'season-sl-1'
  | 'season-bfa-4-post'
  | 'season-bfa-4'
  | 'season-bfa-3-post'
  | 'season-bfa-3'
  | 'season-bfa-2-post'
  | 'season-bfa-2'
  | 'season-bfa-1'
  | 'season-pre-bfa'
  | 'season-post-legion'
  | 'season-7.3.2'
  | 'season-7.3.0'
  | 'season-7.2.5'
  | 'season-7.2.0';

export const enum TScoringSystem {
  NONE = 0,
  V3,
  V3_LEGION_REMIX,
  V4, // Shadowlands Blizzard Scoring
  V5, // Dragonflight Season 1+ Blizzard Scoring
  V6, // Dragonflight Season 2+ Blizzard Scoring
  V7, // Dragonflight Season 4+ Blizzard Scoring
  V8, // War Within beta
  V9, // War Within Season 1+ Blizzard Scoring (back to best of one map)
  V10, // War Within Season 2+ Blizzard Scoring
  V11 // Midnight Season 1+ Blizzard Scoring
}

export function mythicScoreUseAlternateDungeon(scoringSystem: TScoringSystem) {
  return scoringSystem >= TScoringSystem.V4 && scoringSystem <= TScoringSystem.V7;
}

// returns an event id to use for a specific season
export function getSeasonPartitionEventId(seasonId: number, partitionId: number) {
  if (partitionId >= MAX_PARTITIONS_PER_SEASON) {
    throw new Error('MAX_PARTITIONS_PER_SEASON');
  }

  const eventId = partitionId + SEASON_PARTITION_EVENT_START + MAX_PARTITIONS_PER_SEASON * seasonId;

  if (eventId >= SEASON_PARTITION_EVENT_END) {
    throw new Error('SEASON_PARTITION_EVENT_END overflowed');
  }

  return eventId;
}

export enum SeasonPartitionType {
  LIVE = 'live',
  TR = 'tr',
  PTR = 'ptr',
  BETA = 'beta',
  ALPHA = 'alpha',
  REMIX = 'remix'
}

// a partition will be a way to segment data within a season into different blocks
// that can each be ranked and viewed independently. Particularly useful for mid-season
// nerfs and buffs as well as post-seasons where a season change may not actually happen,
// but we need to distinguish before and after.
export type TSeasonPartition = {
  type: SeasonPartitionType;
  eventId: number; // the eventId to associate with this partition
};

export type TSeasonLoadoutFeature =
  | 'artifacts'
  | 'tier'
  | 'legendaries'
  | 'azerite'
  | 'essences'
  | 'talents' // older talent system pre-Dragonflight
  | 'talents_df' // new talent system introduced in Dragonflight
  | 'corruption'
  | 'covenant'
  | 'score';

export type TAffixLevelRange = {
  min: number;
  max?: number;
  affixes: number[]; // the affixes that rotate at this level
};

export type TSeasonDef = {
  id: number;
  slug: TSeasonIdentifier;
  expansionId: number;
  assetExpansionId: number; // where to load assets from for this season
  patchId: number;
  assetPatchId: number;
  blizzardSeasonId: number;
  isMainSeason: boolean; // indicates whether this is a main retail season or not.
  scoringSystem: TScoringSystem;
  name: string;
  short_name: string;
  title: string;
  loadoutFeatures: TSeasonLoadoutFeature[];

  affix?: string; // affix name
  affixId?: number;
  affixThreshold: number[]; // level at which an affix is added
  affixLevelRanges?: TAffixLevelRange[];
  icon?: string; // affix icon
  allowedDungeonPatches?: string[]; // show dungeons from these patches for this affix
  allowedDungeons?: number[]; // only show some dungeons (can be combined with patch)

  partition?: TSeasonPartition;

  skipAffixAlerts?: boolean;
  skipLeaderboards?: boolean; // do not generate ANY leaderboards for this season
  skipSuspiciousChecks?: boolean;

  supportsRouteGeneration?: boolean;
  supportsReplays?: boolean;
  supportsAffixes?: boolean; // whether seasons support affix leaderboard
  supportsSpecs?: boolean; // whether supports filtering by spec id
  supportsFactions?: boolean; // whether supports filtering by faction
  supportsMixedFactions?: boolean; // supports showing mixed factions (9.2.5+)
  supportsTeams?: boolean; // whether it supports the team system
  supportsWeeklies?: boolean; // whether season supports weekly tracking
  supportsCovenants?: boolean; // whether season supports showing covenant data
  supportsRunRaceAndItemLevel?: boolean; // whether season supports loading/saving race and item levels on runs
  supportsScoreStats?: boolean; // whether season supports score stats mysql models
  supportsCutoffsPage?: boolean; // whether season supports showing the score cutoffs page
  supportsRecruitment?: boolean; // whether you can recruit for this season
  supportsWarbands?: boolean; // whether supports warband scores
  supportsHeatmap?: boolean; // whether supports heatmap

  forceShow?: boolean; // force to show even if it's a future patch
  hidden?: boolean;
  allowedRealmTypes?: RealmConnectionType[];

  dataProviders: TActivityDataProvider[];

  sortWeight?: number; // used only for SEASONS_SORTED
} & ITimeBoundsMetadata;

export function getSortedSeasons(seasons: TSeasonDef[]) {
  return [...seasons].sort((a, b) => {
    if (a.blizzardSeasonId && b.blizzardSeasonId && a.blizzardSeasonId !== b.blizzardSeasonId) {
      return b.blizzardSeasonId - a.blizzardSeasonId;
    }

    if (a.sortWeight || b.sortWeight) {
      if (a.sortWeight && b.sortWeight) {
        return b.sortWeight - a.sortWeight;
      } else if (a.sortWeight) {
        return 1;
      } else {
        return -1;
      }
    }

    return b.ends.us.diff(a.ends.us);
  });
}

export function getSeasonByEventId(seasons: TSeasonDef[]) {
  return new Map<number, TSeasonDef>(
    seasons.reduce<[number, TSeasonDef][]>((accumulator: [number, TSeasonDef][], season: TSeasonDef) => {
      if (!season.partition) {
        return accumulator;
      }

      return [...accumulator, [season.partition.eventId, season]];
    }, [])
  );
}

// always returns a season
export function generateGetSeasonBySlug(seasons: TSeasonDef[], seasonBySlug: Record<string, TSeasonDef>) {
  return (slug: string) => {
    if (seasonBySlug[slug]) {
      return seasonBySlug[slug];
    }

    const weekMatch = slug.match(/^([\w-]+)-week-(\d+|current|previous)$/);
    if (weekMatch) {
      const [, season] = weekMatch;
      return seasonBySlug[season] || seasons[0];
    }

    const eventMatch = slug.match(/^([\w-]+)-event-([\w-]+)$/);
    if (eventMatch) {
      const [, season] = eventMatch;
      return seasonBySlug[season] || seasons[0];
    }

    return seasons[0];
  };
}
